/**
 * Sell sheet spec — the fully-resolved data a factsheet renders from.
 *
 * Built from the Adjacent public API (see `worker/services/sell-sheets.ts`) and
 * persisted verbatim on the `sell_sheets` row, so re-opening a historical sheet
 * replays the numbers as they stood at generation time rather than refetching.
 * Levels and returns are numbers, not strings: the renderer owns the accounting
 * formatting.
 *
 * This module is the contract between the builder and the renderer, so the
 * layout capacities both of them depend on live here rather than in either one.
 */

/**
 * One row of the construction table (office category, state, or maturity).
 * A type alias rather than an interface so it carries an implicit index
 * signature and can be passed straight to the renderer's generic table.
 */
export type ConstructionRow = {
  label: string;
  note: string;
  /** Right-aligned count, pre-pluralised (e.g. "34 markets"). */
  markets: string;
  /** Right-aligned share, pre-formatted (e.g. "30.0%"). */
  weight: string;
};

/**
 * The construction section varies by index shape: a composite breaks down by
 * office category, a market-basket sub-index by state exposure (falling back to
 * maturity). `null` when the index supplies neither.
 */
export interface ConstructionSection {
  heading: string;
  rows: ConstructionRow[];
  note: string;
}

export interface FactsheetSpec {
  ticker: string;
  name: string;
  /** Formatted as-of date, e.g. "Jul 21, 2026". */
  asOf: string;
  /** Party the index tracks, as an adjective ("Republican", "Democratic"). */
  party: string;
  /** Party at the opposite end of the scale. */
  counterparty: string;
  /**
   * Published level on the UPFI scale, 50 to 150. The weighted party
   * probability the sheet quotes is `level - 50`; the renderer derives it so
   * the two can never disagree.
   */
  level: number;
  /** Symbol for the probability term in the scale formula. */
  probabilitySymbol: string;
  /** Prose describing what the basket spans, injected into the objective. */
  scope: string;
  trailingReturns: Array<{ period: string; value: number | null }>;
  risk: {
    change30d: number;
    high30d: number;
    low30d: number;
    volatility: number;
  };
  details: Array<{ label: string; value: string }>;
  construction: ConstructionSection | null;
  holdings: Array<{
    ticker: string;
    market: string;
    /** 30-day return in percent. */
    ret: number | null;
    weight: string;
  }>;
  /** Share of index weight held by the listed holdings, e.g. "51.5%". */
  holdingsWeightShare: string;
  totalMarkets: string;
  features: Array<{ title: string; body: string }>;
  methodology: Array<{ term: string; note: string }>;
  historySince: string;
  indexUrl: string;
}

/* ── Layout capacities ────────────────────────────────────────────────────
   Both pages are a fixed 1056px and the tables fill them exactly, so these
   caps are a layout fact rather than a preference. The renderer slices to them
   defensively (a spec replayed from D1 predates any change to them); the
   builder reads them too, for the "X of Y states" prose only it can write.
   ────────────────────────────────────────────────────────────────────── */

/** Top holdings printed on page 2. */
export const HOLDINGS_LIMIT = 10;

/** Construction rows printed on page 2. */
export const CONSTRUCTION_ROW_LIMIT = 6;

/* ── Formatting ───────────────────────────────────────────────────────────
   Accounting convention: negatives in parentheses, never colour. Missing
   values are a hyphen.
   ────────────────────────────────────────────────────────────────────── */

/** The missing-value sentinel, shared by every formatter on the sheet. */
export const EMPTY = "-";

export function pct(value: number | null | undefined, decimals = 2): string {
  if (value == null || !Number.isFinite(value)) return EMPTY;
  const magnitude = `${Math.abs(value).toFixed(decimals)}%`;
  return value < 0 ? `(${magnitude})` : magnitude;
}

export function points(value: number | null | undefined, decimals = 2): string {
  if (value == null || !Number.isFinite(value)) return EMPTY;
  const magnitude = Math.abs(value).toFixed(decimals);
  return value < 0 ? `(${magnitude})` : magnitude;
}

/** A 0-1 fraction as a percentage. Weights are never negative, so no parens. */
export function weightPct(
  weight: number | null | undefined,
  decimals = 1,
): string {
  if (weight == null || !Number.isFinite(weight)) return EMPTY;
  return `${(weight * 100).toFixed(decimals)}%`;
}

/** "1 market" / "34 markets". */
export function marketCount(n: number): string {
  return `${n} ${n === 1 ? "market" : "markets"}`;
}

/** Filename stem for a sheet's PDF, shared by the worker and the client. */
export function sellSheetFilename(ticker: string, asOf: string): string {
  return `${ticker}-Sell-Sheet-${asOf}`
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

/* ── Copy shared by every sheet ───────────────────────────────────────────
   The parts that describe the platform rather than one index. No API source;
   these are deliberate boilerplate, kept together so a copy edit is one file.
   ────────────────────────────────────────────────────────────────────── */

/** Trailing sentence on every composite construction note. */
export const CATEGORY_WEIGHTING =
  "Within each category, eligible markets are weighted by liquidity, time to resolution, and, where relevant, population.";

/** Trailing sentence on every state-exposure note. */
export const STATE_WEIGHTING =
  "Within each state, eligible markets are weighted by liquidity, time to resolution, and population.";

/**
 * Settlement differs by index shape: a composite publishes a trailing 1 hour
 * average of its per-cycle value, while a market-basket sub-index publishes the
 * per-cycle value raw. Smoothing both would smooth the composite twice.
 */
export function methodology(
  isComposite: boolean,
): Array<{ term: string; note: string }> {
  return [
    {
      term: "Price input",
      note: "Trailing 1 minute average of the top-of-book mid, not the last trade print.",
    },
    {
      term: "Eligibility",
      note: "Two-sided book, spread within 50 points, and a quote no more than 4 hours stale.",
    },
    {
      term: "Same race",
      note: "Candidates in one race are summed before weighting, capped at 100.",
    },
    {
      term: "Weighting",
      note: "Liquidity, time to resolution, and population, recomputed every cycle.",
    },
    {
      term: "Category floor",
      note: "A category index halts and carries its last value forward if too few markets price.",
    },
    {
      term: "Settlement",
      note: isComposite
        ? "The composite publishes a trailing 1 hour average of the per-cycle value."
        : "The index publishes its per-cycle value; the 1 hour average applies to composites.",
    },
  ];
}

/** Keyed by the API's `office_category`. */
export const CATEGORY_NOTES: Record<string, string> = {
  presidential: "National contest, highest significance",
  senate: "Committee influence and legislative power",
  house: "Broad political cross-section",
  governor: "State policy and redistricting",
  mayoral: "Local sentiment indicator",
};

/**
 * The key features. `sibling` names the opposite-party index in the
 * "Independent" row; when the index has no sibling in the catalog that row is
 * dropped rather than naming something that may not exist.
 */
export function features(
  ticker: string,
  sibling: string | null,
): Array<{ title: string; body: string }> {
  return [
    {
      title: "Market implied",
      body: "Traded contract prices, not polls or forecasts.",
    },
    {
      title: "Capital at risk",
      body: "Priced by traders with money on the outcome.",
    },
    {
      title: "Continuous",
      body: "Repriced every 60 seconds, not on a survey cycle.",
    },
    { title: "Screened", body: "Only active, two-sided markets qualify." },
    {
      title: "Dynamic weights",
      body: "Favors volume and near-term contracts.",
    },
    {
      title: "Population aware",
      body: "Local races scale with jurisdiction size.",
    },
    ...(sibling
      ? [
          {
            title: "Independent",
            body: `${ticker} and ${sibling} are calculated separately.`,
          },
        ]
      : []),
    {
      title: "Auditable",
      body: "Every value reconciles to its constituent inputs.",
    },
  ];
}
